import { useEffect, useMemo, useState } from 'react'
import { UserCog, Plus, Pencil, Trash2, KeyRound, Search } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '../components/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import { useRoles, type Role } from '../hooks/useRoles'
import { usePermissions } from '../hooks/usePermissions'
import { useAuth } from '../providers/AuthProvider'
import {
  useTenantUsers,
  useCreateTenantUser,
  useUpdateTenantUser,
  useDeleteTenantUser,
  useResetTenantUserPassword,
  errorMessage,
  type TenantUser,
} from '../hooks/useUsers'

const MIN_PASSWORD = 6

function formatDate(value: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('ru-RU')
}

function RoleSelect({
  roles,
  value,
  onChange,
  disabled,
}: {
  roles: Role[]
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger aria-label="Роль">
        <SelectValue placeholder="Выберите роль" />
      </SelectTrigger>
      <SelectContent>
        {roles.map((r) => (
          <SelectItem key={r.id} value={r.id}>
            {r.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Диалог создания / редактирования пользователя. */
function UserEditDialog({
  open,
  onOpenChange,
  user,
  roles,
  isSelf,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  user: TenantUser | null
  roles: Role[]
  isSelf: boolean
}) {
  const createUser = useCreateTenantUser()
  const updateUser = useUpdateTenantUser()

  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [roleId, setRoleId] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setEmail(user?.email ?? '')
    setPhone(user?.phone ?? '')
    setPassword('')
    setRoleId(user?.roleId ?? '')
    setIsActive(user?.isActive ?? true)
  }, [open, user])

  const isSaving = createUser.isPending || updateUser.isPending

  const handleSave = async () => {
    setError(null)
    const trimmedEmail = email.trim()
    if (!trimmedEmail) return setError('Введите email')
    if (!roleId) return setError('Выберите роль')
    if (!user && password.length < MIN_PASSWORD) {
      return setError(`Пароль должен быть не короче ${MIN_PASSWORD} символов`)
    }

    try {
      if (user) {
        await updateUser.mutateAsync({
          id: user.id,
          data: {
            email: trimmedEmail,
            phone: phone.trim() || undefined,
            // Свою роль бэкенд менять не даёт — не отправляем её
            roleId: isSelf ? undefined : roleId,
            isActive: isSelf ? undefined : isActive,
          },
        })
      } else {
        await createUser.mutateAsync({
          email: trimmedEmail,
          phone: phone.trim() || undefined,
          password,
          roleId,
        })
      }
      onOpenChange(false)
    } catch (e) {
      setError(errorMessage(e, 'Не удалось сохранить пользователя'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <div>
            <DialogTitle>{user ? 'Редактирование пользователя' : 'Новый пользователь'}</DialogTitle>
            <DialogDescription>
              Учётная запись для входа в админку и мобильное приложение
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@company.kz"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Телефон <span className="text-gray-400 font-normal">(необязательно)</span>
            </label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+77001234567"
            />
          </div>
          {!user && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Пароль</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`Минимум ${MIN_PASSWORD} символов`}
                autoComplete="new-password"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Роль</label>
            <RoleSelect roles={roles} value={roleId} onChange={setRoleId} disabled={isSelf} />
            {isSelf && (
              <p className="text-xs text-gray-500 mt-1">Свою роль изменить нельзя</p>
            )}
          </div>
          {user && !isSelf && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Активен</span>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          )}
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Отмена
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Сохранение…' : user ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Диалог сброса пароля администратором. */
function ResetPasswordDialog({
  user,
  onClose,
}: {
  user: TenantUser | null
  onClose: () => void
}) {
  const reset = useResetTenantUserPassword()
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    setPassword('')
    setError(null)
    setDone(false)
  }, [user])

  const handleReset = async () => {
    if (!user) return
    if (password.length < MIN_PASSWORD) {
      return setError(`Пароль должен быть не короче ${MIN_PASSWORD} символов`)
    }
    setError(null)
    try {
      await reset.mutateAsync({ id: user.id, password })
      setDone(true)
    } catch (e) {
      setError(errorMessage(e, 'Не удалось сбросить пароль'))
    }
  }

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogClose onClose={onClose} />
        <DialogHeader>
          <div>
            <DialogTitle>Сброс пароля</DialogTitle>
            <DialogDescription>{user?.email}</DialogDescription>
          </div>
        </DialogHeader>
        {done ? (
          <p className="text-sm text-green-700">Пароль изменён. Передайте его пользователю.</p>
        ) : (
          <div className="space-y-2">
            <Input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={`Новый пароль, минимум ${MIN_PASSWORD} символов`}
              autoComplete="new-password"
            />
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          {done ? (
            <Button onClick={onClose}>Готово</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={reset.isPending}>
                Отмена
              </Button>
              <Button onClick={handleReset} disabled={reset.isPending}>
                {reset.isPending ? 'Сохранение…' : 'Сбросить'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function Users() {
  const { user: me } = useAuth()
  const { can } = usePermissions()
  const canEdit = can('users', 'edit')

  const { data: users = [], isLoading, isError, refetch } = useTenantUsers()
  const { data: rolesData } = useRoles({ limit: 100 })
  const roles = useMemo(() => rolesData?.data ?? [], [rolesData])
  const deleteUser = useDeleteTenantUser()

  const [search, setSearch] = useState('')
  const [editOpen, setEditOpen] = useState(false)
  const [selected, setSelected] = useState<TenantUser | null>(null)
  const [toReset, setToReset] = useState<TenantUser | null>(null)
  const [toDelete, setToDelete] = useState<TenantUser | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return users
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.phone ?? '').toLowerCase().includes(q) ||
        (u.role?.name ?? '').toLowerCase().includes(q),
    )
  }, [users, search])

  const openCreate = () => {
    setSelected(null)
    setEditOpen(true)
  }

  const confirmDelete = async () => {
    if (!toDelete) return
    setDeleteError(null)
    try {
      await deleteUser.mutateAsync(toDelete.id)
      setToDelete(null)
    } catch (e) {
      setDeleteError(errorMessage(e, 'Не удалось удалить пользователя'))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <UserCog className="h-8 w-8 text-tco-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Пользователи</h1>
            <p className="text-sm text-gray-500">Учётные записи и их роли</p>
          </div>
        </div>
        {canEdit && (
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Добавить пользователя
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <CardTitle>Пользователи ({filtered.length})</CardTitle>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по email, телефону, роли"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable />
          ) : isError ? (
            <ErrorState title="Не удалось загрузить пользователей" onAction={() => void refetch()} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Телефон</TableHead>
                    <TableHead>Роль</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Последний вход</TableHead>
                    {canEdit && <TableHead className="text-right">Действия</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        Пользователи не найдены
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((u) => {
                      const isSelf = u.id === me?.id
                      return (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">
                            {u.email}
                            {isSelf && <span className="ml-2 text-xs text-gray-400">(вы)</span>}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{u.phone || '—'}</TableCell>
                          <TableCell>{u.role?.name ?? '—'}</TableCell>
                          <TableCell>
                            {u.isActive ? (
                              <Badge variant="success">Активен</Badge>
                            ) : (
                              <Badge variant="secondary">Отключён</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            {formatDate(u.lastLoginAt)}
                          </TableCell>
                          {canEdit && (
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title="Редактировать"
                                  onClick={() => {
                                    setSelected(u)
                                    setEditOpen(true)
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  title="Сбросить пароль"
                                  onClick={() => setToReset(u)}
                                >
                                  <KeyRound className="h-4 w-4" />
                                </Button>
                                {!isSelf && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    title="Удалить"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => {
                                      setDeleteError(null)
                                      setToDelete(u)
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <UserEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        user={selected}
        roles={roles}
        isSelf={!!selected && selected.id === me?.id}
      />

      <ResetPasswordDialog user={toReset} onClose={() => setToReset(null)} />

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div>
              <DialogTitle>Удалить пользователя?</DialogTitle>
              <DialogDescription>
                Учётная запись «{toDelete?.email}» будет удалена, вход по ней станет невозможен.
              </DialogDescription>
            </div>
          </DialogHeader>
          {deleteError && (
            <p className="text-sm text-red-600" role="alert">
              {deleteError}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)} disabled={deleteUser.isPending}>
              Отмена
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDelete}
              disabled={deleteUser.isPending}
            >
              {deleteUser.isPending ? 'Удаление…' : 'Удалить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default Users
